import {
  HouseholdChatPromptTemplate,
  MessageRole,
  MessageType,
  MessageWithPromptTemplate,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import {
  HouseholdChatActivity,
  HouseholdChatMessage,
  HouseholdChatStreamEvent,
} from './householdChat.context';

const TEMP_USER_MESSAGE_PREFIX = 'temp-household-user-';
const TEMP_ASSISTANT_MESSAGE_PREFIX = 'temp-household-assistant-';

/**
 * The stored content of a template message that the user sent without text.
 * Message storage requires non-empty content, so the transcript shows only
 * the template card for this content.
 */
export const TEMPLATE_ONLY_MESSAGE_CONTENT = 'No additional instructions.';

export function isTemporaryAssistantMessage(messageId: string) {
  return messageId.startsWith(TEMP_ASSISTANT_MESSAGE_PREFIX);
}

// Client-created placeholder rows are never interactive messages, so their
// required responseFor connection is always empty; the persisted transcript
// replaces them before any card reads it.
const EMPTY_RESPONSE_FOR: HouseholdChatMessage['responseFor'] = {
  __typename: 'MessageConnection',
  edges: [],
};

// Client-created rows show local files through pendingUploadedImages, so they
// have no stored images.
const EMPTY_USER_UPLOADED_IMAGES: HouseholdChatMessage['userUploadedImages'] = {
  __typename: 'ImageConnection',
  edges: [],
};

type HouseholdChatResult = NonNullable<HouseholdChatStreamEvent['result']>;

export function toConversationMessages(
  conversation:
    | {
        messages?: {
          edges?: Array<{
            node?: HouseholdChatMessage | null;
          } | null> | null;
        } | null;
      }
    | null
    | undefined
) {
  return getNodes(conversation?.messages) ?? [];
}

export function findStreamingMessage(messages: HouseholdChatMessage[]) {
  return messages.find((message) => message.isStreaming) ?? null;
}

export function createOptimisticTurn(
  content: string,
  withPromptTemplate?: HouseholdChatPromptTemplate,
  uploadedFiles?: File[]
) {
  const timestamp = Date.now();
  const userMessageId = `${TEMP_USER_MESSAGE_PREFIX}${timestamp}`;
  const assistantMessageId = `${TEMP_ASSISTANT_MESSAGE_PREFIX}${timestamp}`;

  const optimisticMessages: HouseholdChatMessage[] = [
    {
      __typename: 'Message',
      content,
      id: userMessageId,
      pendingUploadedImages: uploadedFiles,
      responseFor: EMPTY_RESPONSE_FOR,
      userUploadedImages: EMPTY_USER_UPLOADED_IMAGES,
      role: MessageRole.User,
      type: MessageType.Text,
      // The server stores the input template value unchanged. Every input
      // value is also a message value, but the generated enums are distinct.
      withPromptTemplate: withPromptTemplate as string | undefined as
        | MessageWithPromptTemplate
        | undefined,
    },
    createStreamingAssistantMessage(assistantMessageId),
  ];

  return { assistantMessageId, optimisticMessages, userMessageId };
}

export function createStreamingAssistantMessage(
  id = `${TEMP_ASSISTANT_MESSAGE_PREFIX}${Date.now()}`
): HouseholdChatMessage {
  return {
    __typename: 'Message',
    activities: [],
    content: '',
    id,
    isStreaming: true,
    responseFor: EMPTY_RESPONSE_FOR,
    role: MessageRole.Assistant,
    type: MessageType.Text,
    userUploadedImages: EMPTY_USER_UPLOADED_IMAGES,
  };
}

export function appendStreamActivity(
  messages: HouseholdChatMessage[],
  activity: HouseholdChatActivity
) {
  return messages.map((message) =>
    message.isStreaming
      ? {
          ...message,
          activities: [...(message.activities ?? []), activity],
          // An activity after answer deltas means the planner continued into
          // another tool-use tick, so the buffered partial answer is stale.
          content: '',
        }
      : message
  );
}

export function appendStreamText(
  messages: HouseholdChatMessage[],
  textDelta: string
) {
  return messages.map((message) =>
    message.isStreaming
      ? { ...message, content: message.content + textDelta }
      : message
  );
}

export function upsertStreamMessage(
  messages: HouseholdChatMessage[],
  incomingMessage: HouseholdChatMessage
) {
  if (messages.some((message) => message.id === incomingMessage.id)) {
    return messages.map((message) =>
      message.id === incomingMessage.id
        ? { ...message, ...incomingMessage }
        : message
    );
  }

  // Persisted rows created mid-turn belong above the temporary streaming
  // assistant so the in-progress answer stays last in the transcript.
  const placeholderIndex = messages.findIndex((message) => message.isStreaming);
  if (placeholderIndex === -1) {
    return [...messages, incomingMessage];
  }

  return [
    ...messages.slice(0, placeholderIndex),
    incomingMessage,
    ...messages.slice(placeholderIndex),
  ];
}

export function removeOptimisticTurn(
  messages: HouseholdChatMessage[],
  userMessageId: string,
  assistantMessageId: string
) {
  return messages.filter(
    (message) =>
      message.id !== userMessageId && message.id !== assistantMessageId
  );
}

export function toResultMessages(
  result: HouseholdChatResult,
  previousMessages: HouseholdChatMessage[]
) {
  const conversationMessages = toConversationMessages(result.conversation);
  const lastMessage = conversationMessages[conversationMessages.length - 1];
  const activities = findStreamingMessage(previousMessages)?.activities ?? [];

  const augmentedAssistantMessage = {
    __typename: 'Message' as const,
    activities,
    content: result.answer,
    id:
      lastMessage?.role === MessageRole.Assistant
        ? lastMessage.id
        : `${TEMP_ASSISTANT_MESSAGE_PREFIX}result`,
    isStreaming: false,
    responseFor: lastMessage?.responseFor ?? EMPTY_RESPONSE_FOR,
    role: MessageRole.Assistant,
    type:
      lastMessage?.role === MessageRole.Assistant
        ? lastMessage.type
        : MessageType.Text,
    userUploadedImages:
      lastMessage?.userUploadedImages ?? EMPTY_USER_UPLOADED_IMAGES,
  } satisfies HouseholdChatMessage;

  if (lastMessage?.role !== MessageRole.Assistant) {
    return [...conversationMessages, augmentedAssistantMessage];
  }

  return conversationMessages.map((message, index) =>
    index === conversationMessages.length - 1
      ? {
          ...message,
          ...augmentedAssistantMessage,
        }
      : message
  );
}
